import { useState, useCallback } from 'react'
import toNumber from 'lodash/toNumber'
import type { Address, Gps } from '@ztechapps/common.contract'
import { StatusEnum, AddressTypeEnum } from '@ztechapps/common.contract'

interface ApiResponse {
  zip: string
  lat: string
  lng: string
  city: string
  state_id: string
  state_name: string
  county_name: string
  timezone: string
}

type UseAddressByZipResult = [
  (zipCode: string) => Promise<Address | null>,
  {
    loading: boolean
    error: string | null
    data: Address | null
  }
]

const DEFAULT_API_URL = 'https://zip.panelpunches.com/address/zip'

const mapApiResponseToAddress = (data: ApiResponse): Address => {
  const gps: Gps = {
    type: 'Point',
    coordinates: [toNumber(data.lng), toNumber(data.lat)],
  } as Gps

  const address: Partial<Address> = {
    _id: `zip_${data.zip}`,
    zip: data.zip,
    city: data.city,
    state: data.state_id,
    street1: '', // Required field in Address type
    street2: '', // Required field in Address type
    street3: '', // Required field in Address type
    country: 'United States', // Default country
    fullAddress: `${data.city}, ${data.state_id} ${data.zip}, United States`,
    name: '', // Required field in Address type
    phone: '', // Required field in Address type
    email: '', // Required field in Address type
    company: '', // Required field in Address type
    status: StatusEnum.Active, // Using StatusEnum
    type: AddressTypeEnum.personal, // Using AddressTypeEnum
    tags: [], // Required field in Address type
    addressResidentialIndicator: false, // Default value
    gps,
    remarks: `Auto-generated from zip code lookup: ${data.zip}`,
  }

  return address as Address
}

export const useAddressByZip = (baseUrl = DEFAULT_API_URL): UseAddressByZipResult => {
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<Address>(null)

  const fetchAddress = useCallback(
    async (zipCode: string): Promise<Address | null> => {
      if (!zipCode || zipCode.trim() === '') {
        return null
      }

      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`${baseUrl.replace(/\/+$/, '')}/${encodeURIComponent(zipCode)}`)
        const apiResponse = await response.json()

        if (!response.ok || apiResponse.error) {
          throw new Error(apiResponse?.error?.message || 'Unknown error occurred')
        }

        const address = mapApiResponseToAddress(apiResponse)
        setData(address)
        return address
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred'
        setError(errorMessage)
        setData(null)
      } finally {
        setLoading(false)
      }
    },
    [baseUrl]
  )

  return [fetchAddress, { loading, error, data }]
}
